import React from 'react';
import { useController } from 'react-hook-form';

import styled from 'styled-components';

import { Combobox } from '@headlessui/react';
import { LuCheck, LuX } from 'react-icons/lu';

import { Loader } from 'UI';

type Props = {
  id: string;
  onInputChange: any;
  onChange?: any;
  onReset?: any;
  options: any[] | undefined;
  index?: string;
  required?: boolean;
  unactive?: boolean;
  label: string;
  placeholder?: string;
  loading?: boolean;
  customChild?: any;
};

export const Autocomplete = ({
  id,
  onChange,
  onInputChange,
  onReset,
  options,
  index,
  required = false,
  unactive = false,
  label,
  placeholder,
  loading = false,
  customChild: CustomChild,
}: Props): any => {
  const {
    field,
    fieldState: { error },
  } = useController({ name: id });

  const filteredOptions =
    field.value === ''
      ? options
      : options?.filter((option) =>
          (index ? option[index] : option)
            ?.toLowerCase()
            .replace(/\s+/g, '')
            .includes(field.value?.toLowerCase().replace(/\s+/g, '')),
        );

  return (
    <Combobox
      {...field}
      as="div"
      value={field.value}
      onChange={onChange || field.onChange}
      disabled={unactive}
    >
      {({ open }) => {
        return (
          <>
            <Label $error={!!error}>
              {label}
              {required && <span>*</span>}
            </Label>

            <Box $error={!!error} $disabled={unactive}>
              <ComboboxInput
                placeholder={placeholder}
                $error={!!error}
                onChange={(event: any) => onInputChange(event)}
              />
              {!unactive && onReset && (
                <Button onClick={onReset}>
                  <MenuIcon>
                    <LuX />
                  </MenuIcon>
                </Button>
              )}
            </Box>

            <Options static $isOpen={open}>
              {loading ? (
                <NoOption>
                  <Loader size={12} />
                </NoOption>
              ) : filteredOptions?.length === 0 && field.value !== '' ? (
                <NoOption>Nothing found.</NoOption>
              ) : (
                filteredOptions?.map((option: any) => (
                  <Option key={index ? option[index] : option} value={option}>
                    {({ selected }) => (
                      <>
                        {!!CustomChild && <CustomChild option={option} />}
                        <ItemText $selected={selected}>
                          {index ? option[index] : option}
                        </ItemText>
                        {selected ? (
                          <ItemIcon>
                            <LuCheck />
                          </ItemIcon>
                        ) : null}
                      </>
                    )}
                  </Option>
                ))
              )}
            </Options>
          </>
        );
      }}
    </Combobox>
  );
};

const Box = styled.div<{ $error: boolean; $disabled: boolean }>`
  display: flex;
  position: relative;
  width: 100%;
  cursor: pointer;
  overflow: hidden;
  border-radius: 0.5rem;
  text-align: left;
  box-shadow: ${({ $disabled }) =>
    $disabled
      ? 'none'
      : '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'};
`;

const ComboboxInput = styled(Combobox.Input)<{
  placeholder?: string;
  $error: boolean;
}>`
  position: relative;
  width: 100%;
  padding: 0.7rem 2rem 0.7rem 0.75rem;
  font-weight: 500;
  overflow: hidden;
  background-color: white
  box-sizing: border-box;
  font-size: 0.9rem;
  border-radius: 0.5rem;
  border: 0.1rem solid ${({ $error }) => ($error ? 'red' : '#d1d5db')};
  &:focus {
    outline: none;
    border-bottom: 0.2rem solid ${({ $error }) => ($error ? 'red' : '#154077')};
  }
  &:disabled {
    background-color: #d2d7d3;
    opacity: 0.9;
    cursor: default;
  }
`;

const Button = styled(Combobox.Button)`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding-right: 1.8rem;
`;

const Label = styled(Combobox.Label)<{ $error: boolean }>`
  display: block;
  line-height: 1.25rem;
  font-weight: 600;
  font-size: 0.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $error }) => ($error ? 'red' : '#154077')};
  margin-bottom: 0.5rem;
  & > span {
    margin-left: 0.2rem;
  }
`;

const Options = styled(Combobox.Options)<{ $isOpen: boolean }>`
  position: absolute;
  margin-top: 0.25rem;
  max-height: 15rem;
  overflow-y: auto;
  border-radius: 0.375rem;
  background-color: white;
  padding-top: 0.25rem;
  font-size: 1rem;
  line-height: 1.5rem;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  transform: ${({ $isOpen }) => `${$isOpen ? 'scale(1)' : 'scale(0.95)'}`};
  transition: all 0.15s ease-in-out;
  &:focus {
    outline: none;
  }
  z-index: 2;

  @media (max-width: 640px) {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
`;

const Option = styled(Combobox.Option)`
  display: flex;
  position: relative;
  cursor: pointer;
  padding: 0.5rem 0.5rem 0.5rem 2.5rem;
  color: rgb(17, 24, 39);
  border-left: 3px solid transparent;
  &:hover {
    border-left: 3px solid #154077;
    background-color: #ecfccb;
    & > span {
      color: #154077;
    }
  }
  & > span {
    margin-right: 0.5rem;
  }
`;

const NoOption = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  user-select: none;
  padding: 0.5rem 2rem 0.5rem 1rem;
`;

const ItemText = styled.span<{ $selected: boolean }>`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: ${({ $selected }) => ($selected ? '500' : '400')};
`;

const MenuIcon = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  padding-right: 0.5rem;
  top: 0;
  bottom: 0;
  right: 0;
  & > svg {
    color: #aabb00;
    height: 1.25rem;
    width: 1.25rem;
  }
`;

const ItemIcon = styled.span`
  display: flex;
  align-items: center;
  position: absolute;
  padding-left: 0.75rem;
  color: #154077;
  top: 0;
  bottom: 0;
  left: 0;

  & > svg {
    stroke-width: 3;
    height: 1.25rem;
    width: 1.25rem;
  }
`;
